import React, { useMemo, useState } from 'react';
import { PAYMENT_TYPES } from '@/utils/constants';
import { cn } from '@/utils/merge';

// One decimal, without a dangling ".0" — same rounding the page used before.
const num = n => parseFloat((Number(n) || 0).toFixed(1));

// Cents, for the turnover: with one decimal, cards + cash would not always add
// up to the total printed above them.
const cents = n => parseFloat((Number(n) || 0).toFixed(2));

const Stat = ({ label, value, lines, hint, accent, negative }) => (
	<div
		className={cn(
			'flex min-w-0 flex-col rounded-lg border px-3 py-2 shadow-sm',
			accent ? 'border-cyan-600/40 bg-cyan-50/60' : 'border-slate-200 bg-white',
		)}
	>
		<div className='truncate text-[11px] font-semibold tracking-wider text-slate-400 uppercase'>
			{label}
		</div>
		<div
			className={cn(
				'text-lg font-semibold',
				negative ? 'text-red-600' : accent ? 'text-cyan-800' : 'text-slate-700',
			)}
		>
			{value}
		</div>
		{lines?.map(line => (
			<div key={line} className='truncate text-xs text-slate-600'>
				{line}
			</div>
		))}
		{hint ? <div className='truncate text-[11px] text-slate-400'>{hint}</div> : null}
	</div>
);

// Compact per-shop / per-driver table. Columns are summed automatically, so the
// footer always agrees with the tiles above: a row the user excluded is dimmed
// and left out of both.
const Breakdown = ({ title, rows, columns, isExcluded, onToggle, note }) => {
	const [sort, setSort] = useState({ key: 'count', dir: 'desc' });

	const sorted = useMemo(() => {
		const col = columns.find(c => c.key === sort.key);
		const dir = sort.dir === 'asc' ? 1 : -1;
		return [...rows].sort((a, b) => {
			if (col?.text) {
				return dir * String(a[sort.key] ?? '').localeCompare(String(b[sort.key] ?? ''), 'el');
			}
			return dir * ((a[sort.key] || 0) - (b[sort.key] || 0));
		});
	}, [rows, columns, sort]);

	const totals = useMemo(() => {
		const acc = {};
		for (const row of rows) {
			if (isExcluded(row.id)) {
				continue;
			}
			for (const col of columns) {
				if (!col.text) {
					acc[col.key] = (acc[col.key] || 0) + (row[col.key] || 0);
				}
			}
		}
		return acc;
	}, [rows, columns, isExcluded]);

	if (!rows.length) {
		return null;
	}

	const toggleSort = key =>
		setSort(s => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

	return (
		<div className='min-w-0 flex-1'>
			<div className='mb-1 flex flex-row items-baseline gap-x-2'>
				<div className='text-[15px] font-semibold text-slate-600'>{title}</div>
				{note ? <div className='text-[11px] text-slate-400'>{note}</div> : null}
			</div>
			<div className='overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm'>
				<table className='w-full border-collapse text-sm'>
					<thead>
						<tr className='border-b border-slate-200 bg-slate-50'>
							{columns.map(col => (
								<th
									key={col.key}
									onClick={() => toggleSort(col.key)}
									className={cn(
										'cursor-pointer px-2.5 py-1.5 text-[11px] font-semibold tracking-wider whitespace-nowrap text-slate-400 uppercase select-none hover:text-slate-600',
										col.text ? 'text-left' : 'text-right',
									)}
								>
									{col.label}
									{sort.key === col.key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{sorted.map(row => {
							const excluded = isExcluded(row.id);
							return (
								<tr
									key={row.id}
									onClick={() => onToggle(row.id)}
									title={excluded ? 'Συμπερίληψη στα σύνολα' : 'Εξαίρεση από τα σύνολα'}
									className={cn(
										'cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-slate-50',
										excluded && 'text-slate-300 line-through',
									)}
								>
									{columns.map(col => (
										<td
											key={col.key}
											className={cn(
												'px-2.5 py-1 whitespace-nowrap',
												col.text ? 'max-w-52 truncate' : 'text-right tabular-nums',
											)}
										>
											{col.text ? row[col.key] : num(row[col.key])}
										</td>
									))}
								</tr>
							);
						})}
					</tbody>
					<tfoot>
						<tr className='border-t border-slate-200 bg-slate-50 font-semibold text-slate-600'>
							{columns.map(col => (
								<td
									key={col.key}
									className={cn(
										'px-2.5 py-1.5 whitespace-nowrap',
										col.text ? '' : 'text-right tabular-nums',
									)}
								>
									{col.text ? 'Σύνολο' : num(totals[col.key])}
								</td>
							))}
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
};

/**
 * Everything under the completed-orders table: headline numbers, plus the
 * per-driver and per-shop breakdowns.
 *
 * With cursor pagination `orders` holds only the loaded pages, so nothing here
 * may be counted from it. `aggregates.pairs` is the range-wide per-(shop,
 * driver) aggregate and is the only input the numbers need: totals, both
 * breakdowns and every exclusion fall out of it exactly. The two paths below it
 * are for payloads that carry no pairs — a shop account (which gets totals but
 * no breakdowns, and has no exclusion UI) and a backend older than the pairs
 * aggregate.
 */
export const OrderSummary = ({
	orders,
	aggregates,
	deliverygas,
	showFees,
	showShopFees,
	showTime,
	isMasterOrAdmin,
}) => {
	// Kept by id, so they survive a refetch — the reset button is right there.
	const [excludedShops, setExcludedShops] = useState(() => new Set());
	const [excludedDeliveries, setExcludedDeliveries] = useState(() => new Set());

	const toggle = (setter, id) =>
		setter(prev => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});

	const { totals, shopRows, deliveryRows } = useMemo(() => {
		let shops, deliveries, totals;

		const emptyTotals = () => ({
			count: 0,
			price: 0,
			cardPrice: 0,
			fee: 0,
			shopFee: 0,
			gas: 0,
			distance: 0,
			timeSum: 0,
			timeCount: 0,
			excludedCount: 0,
			// Piece work (envelopes, newspapers): pieces delivered.
			pieces: 0,
		});

		if (aggregates?.pairs) {
			// Every number is a range-wide SQL aggregate grouped by the (shop,
			// driver) pair, so excluding a row takes that row's WHOLE range off the
			// totals. The loaded-orders paths below can only subtract the orders that
			// happen to be loaded, which is why a driver with 40 orders in the range
			// but 1 on the first page used to move the totals by 1.
			totals = emptyTotals();
			shops = new Map();
			deliveries = new Map();
			// The named rows exist only to label the ids; all arithmetic is on pairs.
			const shopNameById = new Map((aggregates.shops || []).map(r => [r.id, r.name]));
			const deliveryNameById = new Map((aggregates.deliveries || []).map(r => [r.id, r.name]));

			const bump = (map, id, name, pair, extra) => {
				let row = map.get(id);
				if (!row) {
					row = { id, name, count: 0, price: 0, fee: 0, shopFee: 0, pieces: 0, ...extra };
					map.set(id, row);
				}
				row.count += pair.count;
				row.price += pair.price;
				row.fee += pair.fee;
				row.shopFee += pair.shopFee;
				row.pieces += pair.pieces;
				return row;
			};

			for (const p of aggregates.pairs) {
				// pieces and cardPrice come last, and are absent from older servers.
				const [
					sId,
					dId,
					count,
					price,
					fee,
					shopFee,
					distance,
					timeSum,
					timeCount,
					pieces = 0,
					cardPrice = 0,
				] = p;
				const pair = { count, price, fee, shopFee, pieces };
				const shopOut = sId !== null && excludedShops.has(sId);
				const deliveryOut = dId !== null && excludedDeliveries.has(dId);

				// A row's own exclusion dims it but does not empty it; what the *other*
				// dimension excludes is really gone, so the two tables stay consistent.
				if (sId !== null && !deliveryOut) {
					const row = bump(shops, sId, shopNameById.get(sId) || '-', pair, { margin: 0 });
					row.margin += shopFee - fee;
				}

				if (dId !== null && !shopOut) {
					const row = bump(deliveries, dId, deliveryNameById.get(dId) || '-', pair, {
						gas: 0,
						net: 0,
						shopNet: 0,
					});
					// The two nets carry the fee totals until the gas loop below takes the
					// driver's fuel off both.
					row.net += fee;
					row.shopNet += shopFee;
				}

				if (shopOut || deliveryOut) {
					totals.excludedCount += count;
					continue;
				}

				totals.count += count;
				totals.price += price;
				totals.cardPrice += cardPrice;
				totals.fee += fee;
				totals.shopFee += shopFee;
				totals.distance += distance;
				totals.timeSum += timeSum;
				totals.timeCount += timeCount;
				totals.pieces += pieces;
			}
		} else if (aggregates?.totals) {
			// No pairs in the payload: either a shop account (totals only, and no
			// exclusion UI, so the loop below is a no-op) or a backend older than the
			// pairs aggregate. Seed from the range-wide totals, then subtract what the
			// user excluded — only loaded rows can be subtracted here, so exclusions
			// are approximate until the whole range is loaded.
			const t = aggregates.totals;
			totals = {
				count: t.count || 0,
				price: t.price || 0,
				cardPrice: t.cardPrice || 0,
				fee: t.fee || 0,
				shopFee: t.shopFee || 0,
				gas: 0,
				distance: t.distance || 0,
				timeSum: t.timeSum || 0,
				timeCount: t.timeCount || 0,
				excludedCount: 0,
				pieces: t.pieces || 0,
			};
			shops = new Map(
				(aggregates.shops || []).map(r => [
					r.id,
					{ ...r, margin: (r.shopFee || 0) - (r.fee || 0) },
				]),
			);
			deliveries = new Map(
				(aggregates.deliveries || []).map(r => [
					r.id,
					{ ...r, gas: 0, net: r.fee || 0, shopNet: r.shopFee || 0 },
				]),
			);

			for (const o of orders || []) {
				const shopOut = excludedShops.has(o.sId);
				const deliveryOut = excludedDeliveries.has(o.dId);
				if (!shopOut && !deliveryOut) {
					continue;
				}

				// Mirror of the cross-dimension rule below: a row keeps what it was
				// excluded for itself, but loses what the *other* dimension excluded.
				if (deliveryOut) {
					const row = shops.get(o.sId);
					if (row) {
						row.count--;
						row.price -= o.pr || 0;
						row.fee -= o.fee || 0;
						row.shopFee -= o.shopFee || 0;
						row.margin -= (o.shopFee || 0) - (o.fee || 0);
					}
				}
				if (shopOut) {
					const row = deliveries.get(o.dId);
					if (row) {
						row.count--;
						row.price -= o.pr || 0;
						row.fee -= o.fee || 0;
						row.shopFee -= o.shopFee || 0;
						row.net -= o.fee || 0;
						row.shopNet -= o.shopFee || 0;
					}
				}

				totals.excludedCount++;
				totals.count--;
				totals.price -= o.pr || 0;
				if (o.paymentType === PAYMENT_TYPES.CARD) {
					totals.cardPrice -= o.pr || 0;
				}
				totals.fee -= o.fee || 0;
				totals.shopFee -= o.shopFee || 0;
				totals.distance -= o.distanceMeters || 0;
				if (o.tt !== null && o.tt !== undefined) {
					totals.timeSum -= o.tt;
					totals.timeCount--;
				}
			}

			// A row the other dimension emptied entirely matches the fallback loop
			// below, which would never have created it — and the gas loop must not
			// resurrect it.
			for (const [id, row] of shops) {
				if (row.count <= 0) {
					shops.delete(id);
				}
			}
			for (const [id, row] of deliveries) {
				if (row.count <= 0) {
					deliveries.delete(id);
				}
			}
		} else {
			shops = new Map();
			deliveries = new Map();
			totals = emptyTotals();

			for (const o of orders || []) {
				const shopOut = excludedShops.has(o.sId);
				const deliveryOut = excludedDeliveries.has(o.dId);

				// A row's own exclusion dims it but does not empty it; what the *other*
				// dimension excludes is really gone, so the two tables stay consistent.
				if (o.sh && !deliveryOut) {
					if (!shops.has(o.sId)) {
						shops.set(o.sId, {
							id: o.sId,
							name: o.sh?.name || '-',
							count: 0,
							price: 0,
							fee: 0,
							shopFee: 0,
							margin: 0,
						});
					}
					const row = shops.get(o.sId);
					row.count++;
					row.price += o.pr || 0;
					row.fee += o.fee || 0;
					row.shopFee += o.shopFee || 0;
					row.margin += (o.shopFee || 0) - (o.fee || 0);
				}

				if (o.d?.name && !shopOut) {
					if (!deliveries.has(o.dId)) {
						deliveries.set(o.dId, {
							id: o.dId,
							name: o.d?.name || '-',
							count: 0,
							price: 0,
							fee: 0,
							shopFee: 0,
							gas: 0,
							net: 0,
							shopNet: 0,
						});
					}
					const row = deliveries.get(o.dId);
					row.count++;
					row.price += o.pr || 0;
					row.fee += o.fee || 0;
					row.shopFee += o.shopFee || 0;
					// The two nets carry the fee totals until the gas loop below takes the
					// driver's fuel off both.
					row.net += o.fee || 0;
					row.shopNet += o.shopFee || 0;
				}

				if (shopOut || deliveryOut) {
					totals.excludedCount++;
					continue;
				}

				totals.count++;
				totals.price += o.pr || 0;
				if (o.paymentType === PAYMENT_TYPES.CARD) {
					totals.cardPrice += o.pr || 0;
				}
				totals.fee += o.fee || 0;
				totals.shopFee += o.shopFee || 0;
				totals.distance += o.distanceMeters || 0;
				totals.pieces += o.pcs || 0;
				if (o.tt !== null && o.tt !== undefined) {
					totals.timeSum += o.tt;
					totals.timeCount++;
				}
			}
		}

		// Gas belongs to the driver, not to the order: it only counts for drivers
		// who actually show up in this result set.
		for (const dg of deliverygas || []) {
			const row = deliveries.get(Number(dg?.dId)) || deliveries.get(dg?.dId);
			if (!row) {
				continue;
			}
			row.gas += dg?.gas || 0;
			row.net -= dg?.gas || 0;
			row.shopNet -= dg?.gas || 0;
			if (!excludedDeliveries.has(row.id)) {
				totals.gas += dg?.gas || 0;
			}
		}

		return {
			totals,
			shopRows: [...shops.values()],
			deliveryRows: [...deliveries.values()],
		};
	}, [orders, aggregates, deliverygas, excludedShops, excludedDeliveries]);

	if (!orders?.length) {
		return null;
	}

	const hasGas = isMasterOrAdmin && deliveryRows.some(d => d.gas > 0);
	const hasExclusions = excludedShops.size > 0 || excludedDeliveries.size > 0;
	const avgTime = totals.timeCount ? totals.timeSum / totals.timeCount : 0;
	const avgPrice = totals.count ? totals.price / totals.count : 0;
	// A server from before the split sends no cardPrice: all of it would read as cash.
	const hasCardSplit = !aggregates?.totals || aggregates.totals.cardPrice !== undefined;

	// Only when a city runs both fee systems do the two nets need telling apart.
	const bothFeeSystems = showFees && showShopFees;

	const deliveryColumns = [
		{ key: 'name', label: 'Διανομέας', text: true },
		{ key: 'count', label: 'Παραγγ.' },
		...(deliveryRows.some(r => r.pieces > 0) ? [{ key: 'pieces', label: 'Τεμάχια' }] : []),
		...(showFees ? [{ key: 'fee', label: 'Προμήθεια' }] : []),
		...(showShopFees ? [{ key: 'shopFee', label: 'Χρέωση' }] : []),
		...(hasGas ? [{ key: 'gas', label: 'Βενζίνη' }] : []),
		...(showFees && hasGas
			? [{ key: 'net', label: bothFeeSystems ? 'Καθαρό προμ.' : 'Καθαρό' }]
			: []),
		...(showShopFees && hasGas
			? [{ key: 'shopNet', label: bothFeeSystems ? 'Καθαρό χρ.' : 'Καθαρό' }]
			: []),
	];

	// Piece work: a «Τεμάχια» column only where some row has pieces.
	const shopsHavePieces = shopRows.some(r => r.pieces > 0);
	const shopColumns = [
		{ key: 'name', label: 'Κατάστημα', text: true },
		{ key: 'count', label: 'Παραγγ.' },
		...(shopsHavePieces ? [{ key: 'pieces', label: 'Τεμάχια' }] : []),
		{ key: 'price', label: 'Τζίρος' },
		...(showShopFees ? [{ key: 'shopFee', label: 'Χρέωση' }] : []),
		...(showFees ? [{ key: 'fee', label: 'Προμήθεια' }] : []),
		...(showFees && showShopFees ? [{ key: 'margin', label: 'Μικτό' }] : []),
	];

	return (
		<div className='mt-5 flex flex-col gap-y-4'>
			<div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5'>
				<Stat
					label='Παραγγελίες'
					value={totals.count}
					hint={totals.excludedCount ? `${totals.excludedCount} εκτός συνόλων` : undefined}
				/>
				<Stat
					label='Σύνολο τζίρου'
					value={cents(totals.price)}
					lines={
						hasCardSplit
							? [
									`Κάρτες ${cents(totals.cardPrice)}`,
									`Μετρητά ${cents(totals.price - totals.cardPrice)}`,
								]
							: undefined
					}
					hint={totals.count ? `Μ.Ο. ${num(avgPrice)}` : undefined}
				/>
				{totals.pieces > 0 ? <Stat label='Τεμάχια' value={totals.pieces} /> : null}
				{showFees ? <Stat label='Προμήθειες' value={num(totals.fee)} /> : null}
				{hasGas ? <Stat label='Βενζίνη' value={num(totals.gas)} /> : null}
				{/* The bottom line the office actually reads: whatever this city bills
				through, minus the fuel it pays out. Cities migrated onto shopFee have
				withFees off, so keying this on the rider fee alone would hide it. */}
				{hasGas && (showFees || showShopFees) ? (
					<Stat
						label={showFees ? 'Καθαρό διανομέων' : 'Καθαρό'}
						value={num((showFees ? totals.fee : totals.shopFee) - totals.gas)}
						hint={showFees ? 'Προμήθειες − βενζίνη' : 'Χρεώσεις − βενζίνη'}
						accent
					/>
				) : null}
				{showShopFees ? <Stat label='Χρεώσεις καταστημάτων' value={num(totals.shopFee)} /> : null}
				{showFees && showShopFees && isMasterOrAdmin ? (
					<Stat
						label='Μικτό'
						value={num(totals.shopFee - totals.fee)}
						hint='Χρεώσεις − προμήθειες'
						accent
						negative={totals.shopFee - totals.fee < 0}
					/>
				) : null}
				{showTime && avgTime > 0 ? <Stat label='Μέσος χρόνος' value={`${num(avgTime)}′`} /> : null}
				{isMasterOrAdmin && totals.distance > 0 ? (
					<Stat
						label='Χιλιόμετρα'
						value={num(totals.distance / 1000)}
						hint={totals.count ? `Μ.Ο. ${num(totals.distance / totals.count / 1000)}` : undefined}
					/>
				) : null}
			</div>

			{isMasterOrAdmin && (
				<>
					<div className='flex flex-col gap-4 min-[861px]:flex-row'>
						<Breakdown
							title='Διανομείς'
							note='κλικ σε γραμμή = εξαίρεση από τα σύνολα'
							rows={deliveryRows}
							columns={deliveryColumns}
							isExcluded={id => excludedDeliveries.has(id)}
							onToggle={id => toggle(setExcludedDeliveries, id)}
						/>
						<Breakdown
							title='Καταστήματα'
							note='κλικ σε γραμμή = εξαίρεση από τα σύνολα'
							rows={shopRows}
							columns={shopColumns}
							isExcluded={id => excludedShops.has(id)}
							onToggle={id => toggle(setExcludedShops, id)}
						/>
					</div>

					{/* Below the tables so showing it doesn't push them down. */}
					{hasExclusions && (
						<button
							type='button'
							onClick={() => {
								setExcludedShops(new Set());
								setExcludedDeliveries(new Set());
							}}
							className='self-start rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50'
						>
							Επαναφορά εξαιρέσεων ({excludedShops.size + excludedDeliveries.size})
						</button>
					)}
				</>
			)}
		</div>
	);
};

export default OrderSummary;
